import { API_BASE_URL } from './apiUtils';

/**
 * Saves a breakdown directly from the calculation response with exact structure
 * @param {Object} calculatedResponse - Response from calculate-breakdown endpoint
 * @param {Object} params - Additional parameters (propertyId, styleId, tariffId)
 * @returns {Promise<Object>} - The saved breakdown data
 */
export const saveBreakdownDirectly = async (calculatedResponse, params) => {
  try {
    const { propertyId, styleId, tariffId } = params;
    
    // Validate required fields
    if (!styleId || !tariffId) {
      throw new Error('Style ID and Tariff ID are required');
    }
    
    // Make sure we have a valid calculated response
    if (!calculatedResponse || !calculatedResponse.costs || !calculatedResponse.breakdown) {
      throw new Error('Invalid calculated response format');
    }
    
    // Prepare request data
    const requestData = {
      propertyId,
      styleId,
      tariffId,
      calculatedResponse
    };
    
    console.log('Saving breakdown with direct method to preserve exact structure');
    
    // Make API request to save breakdown
    const response = await fetch(`${API_BASE_URL}/api/v1/e-prorab/repairs/direct-save-breakdown`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestData),
      credentials: 'include'
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to save breakdown');
    }
    
    return data;
  } catch (error) {
    console.error('Error saving breakdown directly:', error);
    throw error;
  }
};

/**
 * Creates a breakdown from a calculate endpoint response
 * This is useful for getting the breakdown ID from the calculated data
 * 
 * @param {Object} calculationResult - The result from the calculate endpoint
 * @param {Object} params - Required parameters
 * @returns {Promise<string>} - The ID of the created breakdown
 */
export const createBreakdownFromCalculation = async (calculationResult, params) => {
  try {
    const savedBreakdown = await saveBreakdownDirectly(calculationResult, params);
    return savedBreakdown.data._id;
  } catch (error) {
    console.error('Failed to create breakdown from calculation', error);
    throw error;
  }
};

/**
 * Utility function to help pass an already calculated breakdown to the backend
 * 
 * @param {Object} calculationData - The breakdown calculation 
 * @param {string} propertyId - Property ID
 * @param {string} styleId - Style ID
 * @param {string} tariffId - Tariff ID
 * @returns {Promise<Object>} The created breakdown
 */
export const saveCalculationToBreakdown = async (calculationData, { propertyId, styleId, tariffId }) => {
  try {
    // First check if we already have a structure with costs and breakdown
    if (calculationData.costs && calculationData.breakdown) {
      console.log('Using direct calculation data structure');
      return await saveBreakdownDirectly(calculationData, { propertyId, styleId, tariffId });
    }
    // Otherwise use the returned structure as-is
    else {
      console.log('Using nested calculation data structure');
      return await saveBreakdownDirectly(
        {
          status: 'success',
          costs: calculationData.costs || {},
          breakdown: calculationData.breakdown || { rooms: [], fixtures: {} }
        }, 
        { propertyId, styleId, tariffId }
      );
    }
  } catch (error) {
    console.error('Error in saveCalculationToBreakdown:', error);
    throw error;
  }
}; 